import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  IconButton,
  Link,
  Text,
  Tooltip,
  useDisclosure,
  usePrevious,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Input, NumberInput, Switch } from '@gamma/ui/form-fields';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import ErrorSharpIcon from '@gamma/ui/icons/ErrorSharpIcon';
import HelpSharpIcon from '@gamma/ui/icons/HelpSharpIcon';
import VisibilityOffSharpIcon from '@gamma/ui/icons/VisibilityOffSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { Panel } from '@gamma/ui/layout';
import { Alert as GammaAlert, Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { isEqual } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { URLS } from '../../../../shared/constants';
import { useLogOperationCall } from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers/AuthProvider';
import {
  CrowdStrikeConfiguration,
  GET_EDR_CONFIGURATION,
  IVerifyEDRConfiguration,
  useDeleteEDRConfiguration,
  useGetEDRConfiguration,
  useGetEDRConfigurationPublic,
  useStoreEDRConfiguration,
  useVerifyEDRConfiguration,
} from '../../../entities/queries';

const { integrations } = i18n.pages;
const {
  save,
  cancel,
  error,
  success,
  warning,
  paloAlto: { verifyError },
} = integrations;

const {
  noGoBack,
  yesDelete,
  enabled: enabledLabel,
  disabled: disabledLabel,
  connectionNotVerified,
  connectionVerified: connectionVerifiedMessage,
} = integrations;

const {
  saveSuccess,
  saveError,
  deleteError,
  deleteSuccess,
  isolateEntity,
  isolateHelpText,
  isolateEnabledAlert,
  verifyConnection,
  deleteResult,
  deleteWarning,
  pollingHelpText,
  enterCredentials,
  crowdStrikeDocumentation,
  crowdStrikeIntegration,
  crowdStrikeFormFields,
  deleteCrowdStrikeConnection,
  detectionsCanNotBeEscalatedToCrowdStrike,
} = integrations.crowdStrike;

const { receiveEntityEnrichmentCrowdstrike } = integrations;

const { CROWDSTRIKE_DOCS } = URLS;

interface CrowdstrikeSettingsCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  edrConfiguration?: CrowdStrikeConfiguration;
}

export const CrowdstrikeSettingsCardModal = ({
  isOpen,
  onClose,
}: CrowdstrikeSettingsCardModalProps) => {
  const showToast = useToast();

  const [formIsReady, setFormIsReady] = useState<boolean>(false);
  const { userRole, setUserPrivileges, userPrivileges, userLicense } =
    useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};
  const getEDRConfigQuery =
    userRole === 'admin'
      ? useGetEDRConfiguration
      : useGetEDRConfigurationPublic;

  const handleClose = () => {
    if (EDRData?.getEDRConfiguration === null) {
      reset(emptyForm);
    } else if (EDRData?.getEDRConfiguration) {
      const { provider_name: _, ...rest } = EDRData.getEDRConfiguration;
      reset({ ...rest });
    }
    onClose();
  };

  const emptyForm = {
    enabled: false,
    edr_action_enabled: false,
    base_url: '',
    client_id: '',
    client_secret: '',
    polling_seconds: 30,
  };
  const {
    loading: EDRConfigLoading,
    error: EDRConfigError,
    data: EDRData,
  } = getEDRConfigQuery({
    fetchPolicy: 'network-only',
    skip: !isOpen,
    variables: {
      provider_name: 'crowdstrike',
    },
    onCompleted: (data) => {
      if (data.getEDRConfiguration === null) {
        reset(emptyForm);
        setFormIsReady(true);
      } else {
        const {
          provider_name: _,
          polling_seconds,
          ...rest
        } = data.getEDRConfiguration;
        reset({ ...rest, polling_seconds: polling_seconds / 60 });
        setFormIsReady(true);
      }
    },
  });

  const client = useApolloClient();

  const logOperationCall = useLogOperationCall();

  const {
    watch,
    control,
    register,
    reset,
    formState: { isDirty, isValid },
  } = useForm({
    mode: 'onChange',
  });

  const formData = watch();
  const previousFormData = usePrevious(formData);

  const {
    isOpen: deleteIsOpen,
    onOpen: deleteOnOpen,
    onClose: deleteOnClose,
  } = useDisclosure();

  const [clientSecretInputPwType, setClientSecretInputPwType] =
    useState<boolean>(true);

  const [configurationVerified, setConfigurationVerified] = useState<
    boolean | undefined
  >();

  const [verifyEDRConfiguration, { loading: verifyEDRConfigurationLoading }] =
    useVerifyEDRConfiguration({
      fetchPolicy: 'network-only',
      onCompleted: (data: IVerifyEDRConfiguration) => {
        setConfigurationVerified(data?.verifyEDRConfiguration?.verified);
      },
      onError: () => {
        showToast({
          title: error,
          description: verifyError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [storeEDRConfiguration, { loading: storeEDRConfigurationLoading }] =
    useStoreEDRConfiguration({
      onCompleted: async () => {
        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'getEDRConfiguration',
        });
        client.cache.gc();
        showToast({
          title: success,
          description: saveSuccess,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
        const updatedInput = {
          status: formData.enabled ? 'Enabled' : 'Disabled',
          service_name: 'CrowdStrike',
        };
        logOperationCall(
          'ENABLE_DISABLE_CONFIGURATION',
          'INTEGRATION',
          JSON.stringify(updatedInput),
        );
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? ''].crowdstrike = formData.enabled;
        newUserPrivileges[tenant ?? ''].crowdstrike_action_enabled =
          formData.edr_action_enabled;
        setUserPrivileges(newUserPrivileges);
        handleClose();
      },
      onError: async () => {
        showToast({
          title: error,
          description: saveError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [deleteEDRConfiguration, { loading: deleteEDRConfigurationLoading }] =
    useDeleteEDRConfiguration({
      variables: {
        provider_name: 'crowdstrike',
      },
      refetchQueries: [
        {
          query: GET_EDR_CONFIGURATION,
          variables: {
            provider_name: 'crowdstrike',
          },
        },
      ],
      onCompleted: async () => {
        showToast({
          title: success,
          description: deleteSuccess,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? ''].crowdstrike = false;
        newUserPrivileges[tenant ?? ''].crowdstrike_action_enabled = false;
        setUserPrivileges(newUserPrivileges);
        reset(emptyForm);
        handleClose();
      },
      onError: async () => {
        showToast({
          title: error,
          description: deleteError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  useEffect(() => {
    if (
      previousFormData !== undefined &&
      !isEqual(previousFormData, formData)
    ) {
      setConfigurationVerified(undefined);
    }
  }, [formData, previousFormData]);

  return EDRConfigError || EDRConfigLoading || !formIsReady ? (
    <GraphQLReqStatus
      error={EDRConfigError}
      loading={EDRConfigLoading || !formIsReady}
      isBackground={true}
    ></GraphQLReqStatus>
  ) : (
    <>
      <Modal
        size="lg"
        isOpen={isOpen}
        onClose={handleClose}
        isCentered={true}
        title={crowdStrikeIntegration}
        closeOnEsc={!verifyEDRConfigurationLoading}
        isCloseDisabled={verifyEDRConfigurationLoading}
        closeOnOverlayClick={!verifyEDRConfigurationLoading}
        body={
          <VStack as="form" alignItems="flex-start" spacing={4}>
            <Text textStyle="body-md">
              {receiveEntityEnrichmentCrowdstrike}
            </Text>
            <Controller
              name="enabled"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Switch
                  inline
                  name={name}
                  isChecked={value}
                  isDisabled={false}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  label={formData.enabled ? enabledLabel : disabledLabel}
                />
              )}
            />
            <Panel layerStyle="second">
              <VStack alignItems="start">
                <Tooltip placement="end" label={isolateHelpText}>
                  <HStack>
                    <Text textStyle="body-md">{isolateEntity}</Text>
                    <HelpSharpIcon color="text.primary" size="sm" />
                  </HStack>
                </Tooltip>
                <Controller
                  name="edr_action_enabled"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <Switch
                      inline
                      name={name}
                      isChecked={value}
                      isDisabled={false}
                      label={
                        formData.edr_action_enabled
                          ? enabledLabel
                          : disabledLabel
                      }
                      onChange={(e) => {
                        onChange(e);
                      }}
                    />
                  )}
                />
                {formData?.edr_action_enabled && (
                  <GammaAlert variant="subtle" status="warning">
                    {isolateEnabledAlert}
                  </GammaAlert>
                )}
              </VStack>
            </Panel>
            <Box>
              <Text>
                {`${enterCredentials} `}
                <Link
                  isExternal
                  href={CROWDSTRIKE_DOCS}
                  textDecoration="underline"
                >
                  {crowdStrikeDocumentation}
                </Link>
                .
              </Text>
            </Box>
            <Input
              {...register('base_url', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={crowdStrikeFormFields.url}
            />
            <Input
              {...register('client_id', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={crowdStrikeFormFields.clientId}
            />
            <Input
              {...register('client_secret', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={crowdStrikeFormFields.clientSecretKey}
              type={clientSecretInputPwType ? 'password' : 'text'}
              rightElement={
                <IconButton
                  cursor="pointer"
                  aria-label={`${
                    clientSecretInputPwType ? 'Show' : 'Hide'
                  } Client Secret`}
                  onClick={() =>
                    setClientSecretInputPwType(!clientSecretInputPwType)
                  }
                >
                  {clientSecretInputPwType ? (
                    <VisibilitySharpIcon size="sm" />
                  ) : (
                    <VisibilityOffSharpIcon size="sm" />
                  )}
                </IconButton>
              }
            />
            <Controller
              name="polling_seconds"
              control={control}
              rules={{
                min: 30,
                max: 1440,
                required: true,
              }}
              render={({ field: { onChange, value, name } }) => (
                <NumberInput
                  min={30}
                  max={1440}
                  name={name}
                  value={value}
                  stepper={true}
                  isRequired={true}
                  isDisabled={false}
                  tooltip={pollingHelpText}
                  hint={crowdStrikeFormFields.minThirtyMinutes}
                  label={crowdStrikeFormFields.pollingMinutes}
                  onChange={(value) => onChange(Number(value))}
                />
              )}
            />
            <ButtonGroup
              w="100%"
              justifyContent="space-between"
              isDisabled={!isValid || configurationVerified}
            >
              <Button
                variant="solid"
                colorScheme="gray"
                leftIcon={<CheckCircleSharpIcon />}
                onClick={() => {
                  verifyEDRConfiguration({
                    variables: {
                      provider_name: 'crowdstrike',
                      config: {
                        crowdstrike: {
                          ...formData,
                          polling_seconds: formData.polling_seconds * 60,
                        },
                      },
                    },
                  });
                }}
                isDisabled={!isValid || configurationVerified}
                isLoading={verifyEDRConfigurationLoading}
              >
                {verifyConnection}
              </Button>
              <Tooltip label={deleteCrowdStrikeConnection}>
                <IconButton
                  variant="solid"
                  colorScheme={deleteEDRConfigurationLoading ? 'gray' : 'red'}
                  onClick={deleteOnOpen}
                  icon={<DeleteSharpIcon />}
                  aria-label={deleteCrowdStrikeConnection}
                  isLoading={deleteEDRConfigurationLoading}
                  isDisabled={deleteEDRConfigurationLoading}
                />
              </Tooltip>
            </ButtonGroup>
            {configurationVerified === true && (
              <Alert status="success" w="100%">
                <Flex>
                  <CheckCircleSharpIcon color="green.100" mr={2} />
                  <Box>
                    <Text textStyle="body-md-bold">
                      {connectionVerifiedMessage}
                    </Text>
                  </Box>
                </Flex>
              </Alert>
            )}
            {configurationVerified === false && (
              <Alert status="error" w="100%">
                <Flex>
                  <WarningSharpIcon color="red.100" mr={2} />
                  <Box>
                    <Text textStyle="body-md-bold">
                      {connectionNotVerified}
                    </Text>
                    <Text>{detectionsCanNotBeEscalatedToCrowdStrike}</Text>
                  </Box>
                </Flex>
              </Alert>
            )}
          </VStack>
        }
        footer={
          <ButtonGroup isDisabled={storeEDRConfigurationLoading}>
            <Button variant="solid" colorScheme="gray" onClick={handleClose}>
              {cancel}
            </Button>
            <Button
              variant="solid"
              onClick={() => {
                storeEDRConfiguration({
                  variables: {
                    provider_name: 'crowdstrike',
                    config: {
                      crowdstrike: {
                        ...formData,
                        polling_seconds: formData.polling_seconds * 60,
                      },
                    },
                  },
                });
              }}
              isLoading={storeEDRConfigurationLoading}
              isDisabled={
                storeEDRConfigurationLoading ||
                !(isDirty && isValid) ||
                (formData?.enabled && !configurationVerified)
              }
            >
              {save}
            </Button>
          </ButtonGroup>
        }
      />
      <Modal
        size="lg"
        isOpen={deleteIsOpen}
        onClose={deleteOnClose}
        title="Delete CrowdStrike Connection"
        body={
          <Alert variant="subtle" status="warning" w="100%">
            <Flex>
              <ErrorSharpIcon color="yellow.100" mr={2} />
              <Box>
                <Text textStyle="body-md-bold">{warning}</Text>
                <Text>{deleteWarning}</Text>
                <Text>{deleteResult}</Text>
              </Box>
            </Flex>
          </Alert>
        }
        footer={
          <ButtonGroup isDisabled={deleteEDRConfigurationLoading}>
            <Button variant="solid" colorScheme="gray" onClick={deleteOnClose}>
              {noGoBack}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={() => {
                deleteEDRConfiguration();
                deleteOnClose();
              }}
              isLoading={deleteEDRConfigurationLoading}
              isDisabled={deleteEDRConfigurationLoading}
            >
              {yesDelete}
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
};
